import {
  DateRange,
  dateRangeOverlapsDate,
  DateString,
  DayOfWeek,
  firstDayOfWeek,
  getHymnOfTheMonthDate,
  lastDayOfWeek,
  lastSundayDate,
  nextSundayDate,
  thisSundayDate,
} from './date_string.ts';
import {
  IntervalSongList,
  IntervalType,
  Org,
  OrgBase,
  OrgType,
  SongList,
  SongListEntry,
  SongListEntryType,
  SongListType,
} from './model.ts';
import { getSongListStartDate, hasInterval } from './model_util.ts';

export const todayTitle = 'Today';
export const thisSundayTitle = 'This Sunday';
const lastSundayTitle = 'Last Sunday';
const nextSundayTitle = 'Next Sunday';

export const staffCanEditEvents = true;

export function getHymnOfTheMonthOrTermTitle(
  hymns: Array<{ psalm?: string; title?: string }>,
  intervalName: string,
  name?: string,
): string {
  if (name) {
    return name;
  }
  const allPsalms =
    hymns.length > 0 &&
    hymns.filter((hymn) => hymn.psalm || hymn.title?.toLowerCase().includes('psalm')).length === hymns.length;
  const prefix = allPsalms ? 'Psalm' : 'Hymn';
  const pluralEnding = hymns.length > 1 ? 's' : '';
  return `${prefix}${pluralEnding} of the ${intervalName}`;
}

export function getSundayTitle(date: DateString, today: DateString) {
  switch (date) {
    case today:
      return todayTitle;
    case thisSundayDate(today):
      return thisSundayTitle;
    case lastSundayDate(today):
      return lastSundayTitle;
    case nextSundayDate(today):
      return nextSundayTitle;
    default:
      return date;
  }
}

export function getWeeklyDateForOrganization(org: OrgBase | undefined, date: DateString): DateString {
  return org?.type === OrgType.Church ? thisSundayDate(date) : firstDayOfWeek(date);
}

export function isWeeklySongList(org: Org | undefined, songList: SongList): boolean {
  return org?.type === OrgType.Church
    ? songList.type === SongListType.WorshipService
    : hasInterval(songList) && songList.interval.type === IntervalType.Week;
}

export function isWeeklySongListForDate(org: Org | undefined, songList: SongList, date?: DateString): boolean {
  return isWeeklySongList(org, songList) && getSongListStartDate(songList) === date;
}

// from the first day of the week of the term start to the last day of the week of the term end
export function getSchoolTermDateRange(schoolTerms: IntervalSongList[], date: DateString): DateRange | undefined {
  for (const { interval } of schoolTerms) {
    if (dateRangeOverlapsDate(interval, date)) {
      return { start: firstDayOfWeek(interval.start), end: lastDayOfWeek(interval.end) };
    }
  }
}

// returns a custom interval song list for the specified date (for printing and copying songs on a specific date)
export function getSongEntriesForDate(
  org: Org,
  date: DateString,
  songLists: SongList[],
  entryTypes: SongListEntryType[],
  { forSingingPlan, forPdfPacket }: { forSingingPlan?: boolean; forPdfPacket?: boolean } = {},
): (SongListEntry & { category?: string })[] {
  const songs: SongListEntry[] = [];
  if (org?.type === OrgType.School) {
    const rows = (org.churchLiturgySchema?.rows ?? []).filter(({ type }) => entryTypes.includes(type));
    for (const { row, intervalType, assigned, label } of rows) {
      if (forSingingPlan && !assigned) {
        continue;
      }
      for (const songList of songLists) {
        if (songList.type === SongListType.Interval && songList.interval.type === intervalType) {
          songs.push(
            ...(songList.songs
              .filter(({ row: currentRow }) => currentRow === row)
              .map((entry) => (forSingingPlan ? { ...entry, category: label } : entry)) ?? []),
          );
        }
      }
    }
  } else {
    if (!forPdfPacket) {
      const hymnOfTheMonth =
        entryTypes.includes(SongListEntryType.Song) &&
        songLists.find(
          (list) => list.type === SongListType.HymnOfTheMonth && list.date === getHymnOfTheMonthDate(date),
        );
      if (hymnOfTheMonth && hymnOfTheMonth.songs.length === 1) {
        songs.push(hymnOfTheMonth.songs[0]);
      }
    }
    songs.push(...(songLists.find((list) => isWeeklySongListForDate(org, list, date))?.songs ?? []));
  }
  return songs;
}

export function isSingingPlanForDate(orgs: Org[], forDate: DateString): boolean {
  return !!orgs.find(({ singingPlan }) => singingPlan?.days.find(({ date }) => date === forDate));
}

export const schoolSingingPlanSkipDays = [DayOfWeek.Saturday, DayOfWeek.Sunday];
export const churchSingingPlanSkipDays = [DayOfWeek.Sunday];
export const singingPlanCompletionDurationMs = 30000;
